<template>
    <p-feature-flag
        name="Sales Reconciliation"
        redirect>
        <b-overlay
            :show="isBusy"
            rounded
            opacity="0.5"
            spinner-small
            spinner-variant="primary"
            >
        <p-form>
            <div class="container-fluid content-wrapper" v-if="salesReconciliation">
                <b-row class="content-heading">
                    <b-col>
                        <div>Manage Sales Reconciliation {{ dateOnlyFormatter(salesReconciliation.salesDate) }}</div>
                    </b-col>
                </b-row>
                <div
                    v-if="salesReconciliation.status === 'Legacy'"
                    class="alert text-error mt-3"
                    style="color: red"
                    role="alert">
                    This record's business-date window hasn't been set, so the sales, tender, and tax totals below are empty. Click <strong>Use Current</strong> below to populate them from this location's current End of Accounting Day.
                </div>
                <p-card>
                    <p-card>
                        <b-row>
                            <b-col>
                                <template v-if="legacyFlow">
                                    <p-button
                                        variant="primary"
                                        :disabled="!usedCurrent || disablePosting"
                                        @click="onPost">Post Sales</p-button>
                                    <p-button
                                        variant="primary"
                                        class="ms-2"
                                        :disabled="usedCurrent"
                                        @click="onUseCurrent">Use Current</p-button>
                                </template>
                                <template v-else>
                                    <p-button
                                        v-if="!isPosted"
                                        variant="primary"
                                        @click="onPost"
                                        :disabled="disablePosting">Post Sales</p-button>
                                    <p-button
                                        v-else
                                        variant="primary"
                                        @click="onUnPost" >Un-Post Sales</p-button>
                                    <p-button
                                        variant="primary"
                                        class="ps-3"
                                        @click="onRefreshSales"
                                        :disabled="isPosted">Refresh Sales</p-button>
                                </template>
                            </b-col>
                            <b-col class="text-center me-auto"><h4>Revenue Collected</h4></b-col>
                            <b-col class="text-center"><h4>Revenue Breakdown</h4></b-col>
                            <b-col class="text-center"><h4>Revenue Difference</h4></b-col>
                        </b-row>
                        <b-row>
                            <b-col>
                            </b-col>
                            <b-col class="text-center"><h4>{{ currencyFormatter(salesReconciliation.revenueCollected) }}</h4></b-col>
                            <b-col class="text-center"><h4>{{ currencyFormatter(salesReconciliation.revenueBreakdown) }}</h4></b-col>
                            <b-col class="text-center"><h4>{{ currencyFormatter(salesReconciliation.revenueDifference) }}</h4></b-col>
                        </b-row>
                    </p-card>                        
                        <b-row v-if="salesReconciliation.productData">
                            <b-col sm="6" md="6" lg="8" xl="9">
                                <p-card>
                                    <p-table
                                        :fields="productFields"
                                        :items="salesReconciliation.productData"
                                        :enable-row-selection="false"
                                        show-per-page-selector
                                        :per-page="50"
                                        table-scroll                                        
                                    >
                                    <template v-slot:title-line><h4>Sales</h4></template>
                                    <template v-slot:abovePaging>
                                        <div class="d-flex pt-1 pb-1">
                                            <span class="text-bold ms-auto">Total:</span>
                                            <span class="text-bold ps-2">{{ 
                                                currencyFormatter(salesReconciliation.productData
                                                .reduce((acc, num) => acc + (num.salesAmount + num.refundsAmount - num.salesDiscount - num.refundsDiscount), 0)) 
                                            }}</span>
                                        </div>
                                    </template>
                                    </p-table>
                                </p-card>
                            </b-col>
                            <b-col sm="6" md="6" lg="4" xl="3">
                                <p-card>
                                    <b-row>
                                        <b-col>
                                            <p-table
                                                :fields="taxAndTenderFields"
                                                :items="salesReconciliation.tenderData"
                                                :show-record-count="false"
                                                :enable-row-selection="false"
                                            >
                                            <template v-slot:title-line><h4>Tenders</h4></template>
                                            </p-table>
                                            <div style="display: flex">
                                                <span class="text-bold ms-auto">Total:</span><span class="text-bold ps-2">{{ currencyFormatter(salesReconciliation.tenderData.reduce((acc, num) => acc + num.amount, 0)) }}</span>
                                            </div>
                                        </b-col>
                                    </b-row>
                                </p-card>
                                <p-card>
                                    <b-row>
                                        <b-col>
                                            <p-table
                                            :fields="taxAndTenderFields"
                                            :items="salesReconciliation.taxData"
                                            :show-record-count="false"
                                            :enable-row-selection="false"                                            
                                            >
                                            <template v-slot:title-line><h4>Taxes</h4></template>
                                            </p-table>
                                            <div style="display: flex">
                                                <span class="text-bold ms-auto">Total:</span><span Class="text-bold ps-2">{{ currencyFormatter(salesReconciliation.taxData.reduce((acc, num) => acc + num.amount, 0)) }}</span>
                                            </div>
                                        </b-col>
                                    </b-row>
                                </p-card>
                            </b-col>
                        </b-row>                    
                    </p-card>
                </div>
            </p-form>
        </b-overlay>
    </p-feature-flag>
</template>

<script>
import axios from 'axios';
import NavigationGuard from '@/components/mixins/NavigationGuard.js';
import { dateOnlyFormatter, currencyFormatter, datetimeToLocalTimeFormatter } from '@/components/Common/Formatters.js';

export default {
    mixins: [NavigationGuard],
    props: {
        id: String
    },
    data() {
        return {
            salesReconciliation: Object,
            productFields: [
                {
                    key: 'skuOrSpecialItemId',
                    label: 'Sale Code',
                    sortable: true
                },
                {
                    key: 'dcc',
                    label: 'DCC',
                    sortable: true
                },
                {
                    key: 'catIsbn',
                    label: 'Cat / ISBN',
                    tdClass: 'noWrap',
                    sortable: true
                },
                {
                    key: 'description',
                    sortable: true
                },
                {
                    key: 'salesQty',
                    sortable: true,
                    tdClass: 'text-end'
                },
                {
                    key: 'salesAmount',
                    sortable: true,
                    formatter: currencyFormatter,
                    tdClass: 'text-end'
                },
                {
                    key: 'salesDiscount',
                    sortable: true,                    
                    formatter: (value) => {
                        return this.currencyFormatter(value != 0 ? value * -1 : value, 0);
                    },
                    tdClass: 'text-end'
                },
                {
                    key: 'refundsQty',
                    sortable: true,
                    tdClass: 'text-end'
                },
                {
                    key: 'refundsAmount',
                    sortable: true,
                    formatter: currencyFormatter,
                    tdClass: 'text-end'
                },
                {
                    key: 'refundsDiscount',
                    sortable: true,
                    formatter: (value) => {
                        return this.currencyFormatter(value != 0 ? value * -1 : value, 0);
                    },
                    tdClass: 'text-end'
                }
            ],
            taxAndTenderFields: [
                {
                    key: 'name',
                    tdClass: 'w-100',
                    sortable: true
                },
                {
                    key: 'amount',
                    sortable: true,
                    formatter: currencyFormatter,
                    tdClass: 'text-end flex-grow-1',
                    thClass: 'text-end'
                },
            ],
            isBusy: false,
            legacyFlow: false,
            usedCurrent: false
        };
    },
    computed:{
        disablePosting() {
            return this.salesReconciliation && (this.salesReconciliation.revenueDifference !== 0 || this.isPosted);
        },
        isPosted() {
            return this.salesReconciliation && this.salesReconciliation.status == 'Posted';
        }        
    },
    methods: {
        currencyFormatter: currencyFormatter,
        dateOnlyFormatter: dateOnlyFormatter,
        async loadData() {
            this.isBusy = true;
            axios.get('salesreconciliation', { params: { id: this.id } })
                .then(resp => {
                    this.salesReconciliation = resp.data;
                    this.legacyFlow = resp.data.status === 'Legacy';
                    this.dataLoaded();
                })
                .finally(() =>
                {
                    this.isBusy = false
                });
        },
        onRefreshSales()
        {
            this.isBusy = true;
            axios.get('salesreconciliation/refreshSales', { params: { id: this.id } })
                .then(resp => {
                    this.salesReconciliation = resp.data;                    
                    this.$appSuccess(
                            `Sales for ${this.dateOnlyFormatter(this.salesReconciliation.salesDate)} refreshed succesfully`
                        )
                    this.dataLoaded();
                })
                .finally(() =>
                {
                    this.isBusy = false
                });
        },
        onPost()
        {
            this.isBusy = true;
            axios.post('salesreconciliation', this.salesReconciliation)
                .then(() => {          
                    this.$appSuccess(
                            `Sales for ${this.dateOnlyFormatter(this.salesReconciliation.salesDate)} posted succesfully`
                        )
                    this.salesReconciliation.status = 'Posted';
                    this.dataLoaded();
                })
                .finally(() =>
                {
                    this.isBusy = false
                });
        },
        onUnPost()
        {
            this.isBusy = true;
            axios.post('salesreconciliation/unPost', this.salesReconciliation)
                .then(resp => {
                    this.salesReconciliation = resp.data;
                    this.$appSuccess(
                            `Sales for ${this.dateOnlyFormatter(this.salesReconciliation.salesDate)} un-posted succesfully`
                        )
                    this.dataLoaded();
                })
                .finally(() =>
                {
                    this.isBusy = false
                });
        },
        onUseCurrent()
        {
            this.$bvModal.msgBoxConfirm(
                `This will apply the current accounting day window to ${this.dateOnlyFormatter(this.salesReconciliation.salesDate)} (start of day: ${datetimeToLocalTimeFormatter(this.salesReconciliation.currentEodWindowStartUtc)}, end of day: ${datetimeToLocalTimeFormatter(this.salesReconciliation.currentEodWindowEndUtc)}). Continue?`
            ).then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.isBusy = true;
                axios.post(`salesreconciliation/useCurrentEod?id=${this.id}`)
                    .then(resp => {
                        this.salesReconciliation = resp.data;
                        this.usedCurrent = true;
                        this.$appSuccess(
                            `${this.dateOnlyFormatter(this.salesReconciliation.salesDate)} populated from the current End of Accounting Day. Review and Post if in balance.`
                        );
                        this.dataLoaded();
                    })
                    .finally(() =>
                    {
                        this.isBusy = false
                    });
            });
        }
    },
    watch: {
        SalesReconciliation: NavigationGuard.$watcher
    },
    mounted: async function() {
        await this.loadData();
    }
}
</script>

<style scoped lang="scss">
:deep(.noWrap ) {
    white-space: nowrap;
}
</style>